import type { DropdownPassThroughMethodOptions } from 'primereact/dropdown';

export const constants = {
  alphaNumericWithSpace: /^[\p{L}\p{N}\s\-_]+$/u,
  alphaWithSpace: /^[\p{L}\s]+$/u,
  address: /^[\p{L}\p{N}\s.,'#\-/&()]+$/u,
  phoneRegex:
    /^\+?\d{1,4}?[-.\s]?\(?\d{1,3}?\)?[-.\s]?\d{1,4}[-.\s]?\d{1,4}[-.\s]?\d{1,9}$/,
  emailRegex:
    /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/,
  tax: 10 / 100,
  defaultCarrierType: { id: 10, name: 'Vehicle' },
  dateFormatUi: 'dd M yy',
  dropdownPtForSpacing: {
    itemLabel(options: DropdownPassThroughMethodOptions) {
      return {
        ...options,
        className: 'flex w-full',
      };
    },
  },
  A4Width: 595.28,
  A4Height: 841.89,
};

export const currencyLocaleMap: Record<string, string> = {
  // 🌎 North America
  USD: 'en-US', // US Dollar
  CAD: 'en-CA', // Canadian Dollar
  MXN: 'es-MX', // Mexican Peso

  // 🌍 Europe
  EUR: 'de-DE', // Euro (Germany used as base)
  GBP: 'en-GB', // British Pound
  CHF: 'de-CH', // Swiss Franc
  SEK: 'sv-SE', // Swedish Krona
  NOK: 'nb-NO', // Norwegian Krone
  DKK: 'da-DK', // Danish Krone
  PLN: 'pl-PL', // Polish Zloty
  CZK: 'cs-CZ', // Czech Koruna
  HUF: 'hu-HU', // Hungarian Forint
  RON: 'ro-RO', // Romanian Leu
  TRY: 'tr-TR', // Turkish Lira
  RUB: 'ru-RU', // Russian Ruble

  // 🌏 Asia-Pacific
  JPY: 'ja-JP', // Japanese Yen
  YEN: 'ja-JP', // Japanese Yen FALLBACK
  CNY: 'zh-CN', // Chinese Yuan
  HKD: 'zh-HK', // Hong Kong Dollar
  TWD: 'zh-TW', // New Taiwan Dollar
  KRW: 'ko-KR', // South Korean Won
  SGD: 'en-SG', // Singapore Dollar
  MYR: 'ms-MY', // Malaysian Ringgit
  THB: 'th-TH', // Thai Baht
  VND: 'vi-VN', // Vietnamese Dong
  IDR: 'id-ID', // Indonesian Rupiah
  PHP: 'en-PH', // Philippine Peso
  AUD: 'en-AU', // Australian Dollar
  NZD: 'en-NZ', // New Zealand Dollar
  INR: 'hi-IN', // Indian Rupee
  PKR: 'ur-PK', // Pakistani Rupee
  BDT: 'bn-BD', // Bangladeshi Taka

  // 🌍 Middle East & Africa
  AED: 'ar-AE', // UAE Dirham
  SAR: 'ar-SA', // Saudi Riyal
  QAR: 'ar-QA', // Qatari Riyal
  EGP: 'ar-EG', // Egyptian Pound
  ZAR: 'en-ZA', // South African Rand
  NGN: 'en-NG', // Nigerian Naira
  KES: 'en-KE', // Kenyan Shilling
  MAD: 'ar-MA', // Moroccan Dirham

  // 🌎 Latin America
  BRL: 'pt-BR', // Brazilian Real
  ARS: 'es-AR', // Argentine Peso
  CLP: 'es-CL', // Chilean Peso
  COP: 'es-CO', // Colombian Peso
  PEN: 'es-PE', // Peruvian Sol

  // 🇪🇺 Others
  ISK: 'is-IS', // Icelandic Krona
  HRK: 'hr-HR', // Croatian Kuna
  BGN: 'bg-BG', // Bulgarian Lev

  // Default fallback if not matched
  DEFAULT: 'en-US',
};

export const flagUnicodeMapping = {
  US: '🇺🇸',
  CA: '🇨🇦',
  GB: '🇬🇧',
  FR: '🇫🇷',
  DE: '🇩🇪',
  IT: '🇮🇹',
  ES: '🇪🇸',
  PT: '🇵🇹',
  NL: '🇳🇱',
  BE: '🇧🇪',
  LU: '🇱🇺',
  CH: '🇨🇭',
  AT: '🇦🇹',
  DK: '🇩🇰',
  NO: '🇳🇴',
  SE: '🇸🇪',
  FI: '🇫🇮',
  IE: '🇮🇪',
  AU: '🇦🇺',
  NZ: '🇳🇿',
  JP: '🇯🇵',
  KR: '🇰🇷',
  CN: '🇨🇳',
  HK: '🇭🇰',
  SG: '🇸🇬',
  IN: '🇮🇳',
  MX: '🇲🇽',
  BR: '🇧🇷',
  AR: '🇦🇷',
  CL: '🇨🇱',
  ZA: '🇿🇦',
  NG: '🇳🇬',
  EG: '🇪🇬',
  SA: '🇸🇦',
  AE: '🇦🇪',
  RU: '🇷🇺',
  UA: '🇺🇦',
  PL: '🇵🇱',
  CZ: '🇨🇿',
  SK: '🇸🇰',
  HU: '🇭🇺',
  RO: '🇷🇴',
  BG: '🇧🇬',
  GR: '🇬🇷',
  TR: '🇹🇷',
};

export const LANGUAGES = [
  {
    name: 'English',
    img: '/flags/Flag_of_the_United_Kingdom.svg',
    code: 'en',
  },
  {
    name: '日本語',
    img: '/flags/Flag_of_Japan.svg.webp',
    code: 'ja',
  },
];

export const MONTHS = {
  JAN: 1,
  FEB: 2,
  MAR: 3,
  APR: 4,
  MAY: 5,
  JUN: 6,
  JUL: 7,
  AUG: 8,
  SEP: 9,
  OCT: 10,
  NOV: 11,
  DEC: 12,
};

export const videoExtensions = [
  'mp4',
  'mov',
  'avi',
  'mkv',
  'webm',
  'flv',
  'wmv',
  'mpeg',
  '3gp',
];

export const imageExtensions = [
  'jpg',
  'jpeg',
  'png',
  'gif',
  'bmp',
  'webp',
  'tiff',
];

export const tenantLogo = '/tenant/images/logo.png';
export const tenantSign = '/tenant/images/sign.png';
